import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { EarningsResponse } from '../../services/api';
import { FY_MONTHS } from './fy';

const PENDING_COPY = 'Receipt not generated / Payment not received';

const inr = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 2 });

const dmy = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const baseUrl = (): string =>
  (process.env.REACT_APP_PUBLIC_URL || window.location.origin).replace(/\/$/, '');

export const exportEarningsPdf = (data: EarningsResponse, companyName?: string): void => {
  const { rows, summary, fy, month } = data;
  const monthLabel = month ? FY_MONTHS.find((m) => m.value === month)?.label : null;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const origin = baseUrl();

  // Header
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Earnings Report', 40, 40);
  let headerY = 58;
  if (companyName) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(companyName, 40, headerY);
    headerY += 16;
  }
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Financial Year: ${fy}${monthLabel ? ` — ${monthLabel}` : ''}`, 40, headerY);
  headerY += 14;
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 40, headerY);
  doc.setTextColor(0);

  // Link targets per row (indexes align with body rows)
  const invoiceLinks = rows.map((r) => (r.invoiceShareToken ? `${origin}/share/invoice/${r.invoiceShareToken}` : null));
  const receiptLinks = rows.map((r) => (r.receiptShareToken ? `${origin}/share/receipt/${r.receiptShareToken}` : null));

  autoTable(doc, {
    startY: headerY + 16,
    head: [[
      'Invoice No.', 'Invoice Date', 'Receipt Number', 'Receipt Date', 'Client Name', 'GST Details',
      'Taxable Amount', 'Tax Amount', 'TDS Deducted', 'Total Amount', 'Invoice Link', 'Receipt Link',
    ]],
    body: (() => {
      const seen = new Set<string>();
      return rows.map((r) => {
        const isRepeat = r.invoiceId !== null && seen.has(r.invoiceId);
        if (r.invoiceId !== null) seen.add(r.invoiceId);
        return [
          r.invoiceNumber,
          dmy(r.invoiceDate) || '—',
          r.receiptNumber ?? PENDING_COPY,
          dmy(r.receiptDate) || '—',
          r.clientName,
          r.clientGstin || '—',
          isRepeat ? '—' : inr(r.taxableAmount),
          isRepeat ? '—' : inr(r.taxAmount),
          r.tdsDeducted ? inr(r.tdsDeducted) : '—',
          isRepeat ? '—' : inr(r.totalAmount),
          r.invoiceId ? 'View Invoice' : '—',
          r.receiptId ? 'View Receipt' : '—',
        ];
      });
    })(),
    foot: [[
      { content: `Totals (${summary.totalInvoices} invoices, ${summary.totalReceipts} receipts, ${summary.pendingReceipts} pending)`, colSpan: 6 },
      inr(summary.totalTaxable), inr(summary.totalTax), inr(summary.totalTds), inr(summary.totalAmount),
      '', '',
    ]],
    styles: { fontSize: 7.5, cellPadding: 4, overflow: 'linebreak' },
    headStyles: { fillColor: [79, 70, 229], fontSize: 7.5, fontStyle: 'bold' },
    footStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold' },
    columnStyles: {
      6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' },
      10: { textColor: [79, 70, 229] }, 11: { textColor: [79, 70, 229] },
    },
    didDrawCell: (hook) => {
      if (hook.section !== 'body') return;
      const links = hook.column.index === 10 ? invoiceLinks : hook.column.index === 11 ? receiptLinks : null;
      if (!links) return;
      const url = links[hook.row.index];
      if (url) {
        doc.link(hook.cell.x, hook.cell.y, hook.cell.width, hook.cell.height, { url });
      }
    },
  });

  // Summary block below table (with page-overflow guard)
  const pageHeight = doc.internal.pageSize.getHeight();
  let afterTable = (doc as any).lastAutoTable.finalY + 24;
  const summaryHeight = 16 + 8 * 13; // heading offset + 8 lines
  if (afterTable + summaryHeight > pageHeight - 40) {
    doc.addPage();
    afterTable = 40;
  }
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Summary', 40, afterTable);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const lines = [
    `Total Invoices: ${summary.totalInvoices}`,
    `Total Receipts: ${summary.totalReceipts}`,
    `Pending Receipts: ${summary.pendingReceipts}`,
    `Total Taxable: Rs. ${inr(summary.totalTaxable)}`,
    `Total Tax: Rs. ${inr(summary.totalTax)}`,
    `Total TDS Deducted: Rs. ${inr(summary.totalTds)}`,
    `Total Amount: Rs. ${inr(summary.totalAmount)}`,
    `Total Received: Rs. ${inr(summary.totalReceived)}`,
  ];
  lines.forEach((l, i) => doc.text(l, 40, afterTable + 16 + i * 13));

  doc.save(`Earnings_FY${fy}${monthLabel ? `_${monthLabel}` : ''}.pdf`);
};
